import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw, Plus, Trash2, Copy, Link2, AlertTriangle, CheckCircle2, Loader2, Globe,
} from "lucide-react";

const SOURCE_COLORS = {
  booking: "bg-[#003580] text-white",
  airbnb: "bg-[#FF5A5F] text-white",
  other: "bg-[#495057] text-white",
};

const CopyRow = ({ label, url, testid }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] uppercase tracking-wider font-bold text-[#868E96] w-[70px] shrink-0">{label}</span>
    <code className="text-[10px] bg-[#F8F9FA] border border-[#DEE2E6] px-2 py-1 flex-1 truncate">{url}</code>
    <Button size="icon" variant="outline" data-testid={testid} className="h-8 w-8 shrink-0"
            onClick={() => {
              navigator.clipboard?.writeText(url);
              toast.success("URL copiée — collez-la dans la plateforme");
            }}>
      <Copy className="h-3 w-3" />
    </Button>
  </div>
);

export const ReservationChannels = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ unit_id: "", source: "booking", url: "", label: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/chef/minisite/channels");
      setData(d);
      setForm((f) => ({ ...f, unit_id: f.unit_id || d.units?.[0]?.id || "" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addFeed = async () => {
    if (!form.url.trim() || !form.unit_id) { toast.error("Choisissez une unité et collez l'URL iCal"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/chef/minisite/channels/feeds", form);
      toast.success(`${r.result.imported} date(s) importée(s) depuis ${r.feed.label}`);
      if ((r.result.conflicts || []).length > 0) {
        toast.error(`${r.result.conflicts.length} conflit(s) : des dates sont déjà réservées chez vous`);
      }
      setForm({ ...form, url: "", label: "" });
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeFeed = async (fid) => {
    setBusy(true);
    try {
      const { data: r } = await api.delete(`/chef/minisite/channels/feeds/${fid}`);
      toast.success(`Flux retiré (${r.blocks_removed} date(s) libérée(s))`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/chef/minisite/channels/sync");
      toast.success(`${r.imported} date(s) synchronisée(s) sur ${r.feeds.length} flux`);
      if (r.conflicts > 0) toast.error(`${r.conflicts} conflit(s) détecté(s)`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-[#868E96]" data-testid="ch-loading">Chargement des canaux…</div>;

  const conflicts = data.units.flatMap((u) => u.feeds.flatMap((f) => (f.conflicts || [])
    .map((c) => ({ ...c, unit: u.label, feed: f.label }))));

  return (
    <div className="space-y-5" data-testid="cr-channels">
      <div className="border border-[#DEE2E6] bg-white p-4">
        <div className="flex flex-wrap items-start gap-3">
          <Globe className="h-6 w-6 text-[#002FA7] shrink-0" />
          <div className="flex-1 min-w-[260px] text-sm text-[#495057]">
            <b className="text-[#0A0A0A]">Deux calendriers à échanger, une seule fois.</b> Collez ci-dessous
            l'URL iCal de chaque annonce Booking ou Airbnb : les dates vendues là-bas se ferment
            automatiquement chez vous. Puis copiez l'URL PAG de l'unité et collez-la chez la plateforme
            pour qu'elle bloque ce que vous vendez ici. Aucune clé d'API n'est nécessaire.
            <div className="text-[11px] text-[#868E96] mt-1">
              Synchronisation automatique toutes les 2 heures.
              {data.last_sync_at ? ` Dernière : ${new Date(data.last_sync_at).toLocaleString("fr-FR")}.` : ""}
            </div>
          </div>
          <Button onClick={syncNow} disabled={busy} data-testid="ch-sync-now" className="bg-[#002FA7] text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Synchroniser maintenant
          </Button>
        </div>
      </div>

      {conflicts.length > 0 && (
        <div className="border-2 border-[#E60000] bg-[#FFF5F5] p-4" data-testid="ch-conflicts">
          <div className="font-display font-bold text-sm flex items-center gap-2 text-[#E60000]">
            <AlertTriangle className="h-4 w-4" />
            {conflicts.length} double réservation détectée
          </div>
          <div className="text-xs text-[#495057] mt-1">
            Ces dates sont vendues sur une plateforme <b>et</b> réservées chez vous. Contactez un
            des deux clients au plus vite.
          </div>
          <ul className="mt-2 text-xs space-y-1">
            {conflicts.map((c, i) => (
              <li key={i} data-testid={`ch-conflict-${i}`}>
                <b>{c.unit}</b> · {c.dates} · {c.feed} vs {c.reference} ({c.client_nom})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Ajouter un flux */}
      <div className="border border-[#DEE2E6] bg-white p-4" data-testid="ch-add">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] mb-2">
          Importer un calendrier
        </div>
        <div className="grid md:grid-cols-[1fr_150px_2fr_auto] gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Unité</Label>
            <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
              <SelectTrigger data-testid="ch-unit"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                {data.units.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Plateforme</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="ch-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(data.sources).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider flex items-center gap-1">
              <Link2 className="h-3 w-3" />URL iCal de l'annonce
            </Label>
            <Input value={form.url} placeholder="https://www.airbnb.fr/calendar/ical/…"
                   data-testid="ch-url" onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <Button onClick={addFeed} disabled={busy} data-testid="ch-add-save" className="h-10">
            <Plus className="h-4 w-4 mr-1" />Importer
          </Button>
        </div>
        <div className="text-[11px] text-[#868E96] mt-2">
          Airbnb : Calendrier → Disponibilités → Synchroniser les calendriers → Exporter.
          Booking.com : Tarifs & disponibilité → Synchronisation de calendrier.
        </div>
      </div>

      {/* Par unité */}
      <div className="space-y-3">
        {data.units.map((u) => (
          <div key={u.id} className="border border-[#DEE2E6] bg-white p-4" data-testid={`ch-unit-${u.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-display font-black text-lg tracking-tight">{u.label}</div>
              {u.feeds.length === 0 && (
                <span className="text-[10px] uppercase tracking-wider text-[#868E96]">aucun calendrier importé</span>
              )}
            </div>

            {u.feeds.length > 0 && (
              <div className="mt-2 space-y-1">
                {u.feeds.map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-[#F1F3F5] pb-1"
                       data-testid={`ch-feed-${f.id}`}>
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 ${SOURCE_COLORS[f.source] || SOURCE_COLORS.other}`}>
                      {f.source}
                    </span>
                    <span className="font-bold">{f.label}</span>
                    <span className="text-[#868E96] truncate max-w-[280px]">{f.url}</span>
                    <span className="flex items-center gap-1 ml-auto">
                      {f.last_status === "ok"
                        ? <CheckCircle2 className="h-3 w-3 text-[#059669]" />
                        : <AlertTriangle className="h-3 w-3 text-[#E60000]" />}
                      {f.events_count} date(s)
                    </span>
                    <Button size="icon" variant="outline" className="h-7 w-7 text-[#E60000]"
                            data-testid={`ch-feed-del-${f.id}`} onClick={() => removeFeed(f.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">
                URL à coller chez la plateforme
              </div>
              <CopyRow label="Airbnb" url={u.export_urls.airbnb} testid={`ch-copy-airbnb-${u.id}`} />
              <CopyRow label="Booking" url={u.export_urls.booking} testid={`ch-copy-booking-${u.id}`} />
              <CopyRow label="Autre" url={u.export_urls.all} testid={`ch-copy-all-${u.id}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
